import { Component, computed, inject, OnDestroy, OnInit, signal, Signal } from '@angular/core';
import { ToastService } from '../../../../_services/toast-service/toast.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { CancelSubscriptionModalComponent } from './cancel-subscription-modal/cancel-subscription-modal.component';
import {
  CurrentSubscription,
  PaymentMethod,
  SubscriptionFrequency,
  SubscriptionPayload,
  SubscriptionProRata,
} from '../../../../_interfaces/subscription.interface';
import { SubscriptionApiService } from '../../../../_services/subscription-service/api/subscription-api-service.service';
import { EmptyIndicatorPipe } from '../../../../shared/pipes/empty-indicator.pipe';
import { OffcanvasService } from '../../../../_services/offcanvas-service/offcanvas.service';
import { SubscriptionService } from '../../../../_services/subscription-service/subscription.service';
import { Subscription } from 'rxjs';
import { OffcanvasBaseComponent } from '../../../../shared/components/offcanvas/offcanvas-base/offcanvas-base.component';
import { PaymentMethodComponent } from '../components/payment-method/payment-method.component';

@Component({
  selector: 'app-manage-plan',
  imports: [CurrencyPipe, DatePipe, EmptyIndicatorPipe, OffcanvasBaseComponent, PaymentMethodComponent],
  providers: [DatePipe],
  templateUrl: './manage-plan.component.html',
})
export class ManagePlanComponent implements OnInit, OnDestroy {
  readonly toastService = inject(ToastService);
  readonly subscriptionApiService = inject(SubscriptionApiService);
  readonly subscriptionService = inject(SubscriptionService);
  readonly offcanvasService = inject(OffcanvasService);
  readonly datePipe = inject(DatePipe);

  readonly subscriptions: Subscription = new Subscription();
  protected currentSubscription!: CurrentSubscription;
  protected proRataAmount?: SubscriptionProRata;
  protected billingFrequency = signal<SubscriptionFrequency>(SubscriptionFrequency.MONTHLY);
  protected payfastToken = signal<string>('');
  protected paymentMethod = signal<PaymentMethod>({} as PaymentMethod);
  private packageId!: number;
  private packageName!: string;
  protected savings!: number;
  protected returnUrl: string = '/your-plan';
  protected readonly SubscriptionFrequency = SubscriptionFrequency;
  readonly modalService = inject(NgbModal);
  protected upgradesAvailable = signal(false);

  isYearly = computed(() => this.billingFrequency() === SubscriptionFrequency.YEARLY);

  ngOnInit(): void {
    this.getSubscriptionDetails();
    this.subscriptions.add(
      this.subscriptionApiService.getUpgradeSubscription().subscribe({
        next: (res) => {
          this.upgradesAvailable.set(!!res.length);
        },
      }),
    );
  }

  getSubscriptionDetails() {
    this.subscriptionApiService.getCurrentSubscription().subscribe({
      next: (res) => {
        this.payfastToken.set(res.payfast_token);
        this.paymentMethod.set(res.payment_method);
        this.currentSubscription = res;
        if (res.package) {
          this.packageId = res.package.id;
          this.packageName = res.package.name;
          this.savings = this.subscriptionService.calculateSavings(res.package.base_cost, res.package.yearly_cost);
        }
        this.getProRataAmount();
      },
    });
  }

  getProRataAmount() {
    this.subscriptionService.getProRataAmount(this.packageId, this.billingFrequency()).subscribe({
      next: (res) => {
        this.proRataAmount = res;
      },
    });
  }

  // Disable the upgrade button if:
  // 1. No upgrades are available.
  // 2. The current subscription is billed yearly, or the yearly toggle is off.
  protected isUpgradeBtnDisabled: Signal<boolean> = computed(() => {
    return (
      !this.upgradesAvailable() &&
      (this.currentSubscription.frequency === SubscriptionFrequency.YEARLY || !this.isYearly())
    );
  });

  onUpgradePackage() {
    this.offcanvasService.offCanvasNavigate('your-plan/upgrade-plan');
  }

  onUpgradeConfirm() {
    const frequency = this.billingFrequency();
    const data: SubscriptionPayload = {
      package: this.packageId,
      frequency: frequency,
    };
    this.subscriptionService.onUpgradePackage(data, this.packageName);
  }

  openModal() {
    const modalRef = this.modalService.open(CancelSubscriptionModalComponent, {
      centered: true,
      size: 'md',
    });
    modalRef.closed.subscribe({
      next: () => {
        const selectedReason = modalRef.componentInstance.selectedReason;
        const data = { reason: selectedReason };
        this.subscriptionApiService.cancelSubscription(data).subscribe({
          next: (res) => {
            const endDate = res.date_end;
            this.showToast(endDate);
            this.offcanvasService.close('/your-plan');
          },
          error: (err) => {
            this.toastService.show(err.error?.non_field_errors[0], 'iconoir-warning-circle');
          },
        });
      },
    });
  }

  showToast(date_end: string) {
    const formattedDate = this.datePipe.transform(date_end, 'dd MMMM yyyy'); // Customize format here
    this.toastService.show(
      `Subscription has been cancelled! Access Paystashio account till ${formattedDate}.`,
      'iconoir-check-circle',
    );
  }

  handleFrequencyChange(event: SubscriptionFrequency) {
    this.billingFrequency.set(event);
    this.getProRataAmount();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
